import React, { useState, useEffect } from 'react';
import { X, Send, Loader2, CheckCircle, User, Mail, Phone, Globe, Briefcase, MessageSquare } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const API_BASE = process.env.REACT_APP_API_URL || '';

const APPLY_T = {
  en: {
    title:        'Apply to Become a Dealer',
    subtitle:     'Fill out the form below and our team will review your application within 24 hours.',
    name:         'Full Name',
    email:        'Email Address',
    phone:        'Phone Number',
    country:      'Country / Region',
    businessType: 'Business Type',
    message:      'Tell us about your business and goals',
    submit:       'SUBMIT APPLICATION',
    submitting:   'SUBMITTING...',
    successTitle: 'Application Submitted!',
    successDesc:  'Thank you for your interest. Our team will review your application and contact you within 24 hours.',
    successClose: 'CLOSE',
    required:     'This field is required',
    invalidEmail: 'Please enter a valid email',
    businessTypes: ['Fleet Management', 'Auto Service / Workshop', 'Distributor / Wholesale', 'Logistics Company', 'Agriculture', 'Other'],
  },
  es: {
    title:        'Solicitar ser Distribuidor',
    subtitle:     'Completa el formulario y nuestro equipo revisara tu solicitud en 24 horas.',
    name:         'Nombre completo',
    email:        'Correo electronico',
    phone:        'Numero de telefono',
    country:      'Pais / Region',
    businessType: 'Tipo de negocio',
    message:      'Cuentanos sobre tu negocio y objetivos',
    submit:       'ENVIAR SOLICITUD',
    submitting:   'ENVIANDO...',
    successTitle: 'Solicitud enviada!',
    successDesc:  'Gracias por tu interes. Nuestro equipo revisara tu solicitud y se comunicara contigo en 24 horas.',
    successClose: 'CERRAR',
    required:     'Este campo es obligatorio',
    invalidEmail: 'Introduce un correo valido',
    businessTypes: ['Gestion de flotas', 'Taller / Servicio automotriz', 'Distribuidor / Mayorista', 'Empresa logistica', 'Agricultura', 'Otro'],
  },
  he: {
    title:        'הגש בקשה להיות סוכן',
    subtitle:     'מלא את הטופס למטה והצוות שלנו יבדוק את בקשתך תוך 24 שעות.',
    name:         'שם מלא',
    email:        'כתובת אימייל',
    phone:        'מספר טלפון',
    country:      'מדינה / אזור',
    businessType: 'סוג עסק',
    message:      'ספר לנו על העסק והמטרות שלך',
    submit:       'שלח בקשה',
    submitting:   '...שולח',
    successTitle: '!הבקשה נשלחה',
    successDesc:  'תודה על העניין. הצוות שלנו יבדוק את בקשתך וייצור איתך קשר תוך 24 שעות.',
    successClose: 'סגור',
    required:     'שדה חובה',
    invalidEmail: 'הזן כתובת אימייל תקינה',
    businessTypes: ['ניהול ציי רכב', 'מוסך / שירות רכב', 'מפיץ / סיטונאי', 'חברת לוגיסטיקה', 'חקלאות', 'אחר'],
  },
};

const LABEL_STYLE = { fontFamily: 'Share Tech Mono' };
const FIELD_ICONS = { name: User, email: Mail, phone: Phone, country: Globe, businessType: Briefcase, message: MessageSquare };

function FormField({ name, type = 'text', value, onChange, placeholder, icon: Icon, error, options }) {
  const base = 'w-full py-3 bg-white/5 border text-white text-sm placeholder-white/25 focus:outline-none transition-colors';
  const borderCls = error ? 'border-red-500/40 focus:border-red-500/60' : 'border-[#00f0ff]/15 focus:border-[#00f0ff]/50';

  if (options) {
    return (
      <div>
        <div className="relative">
          <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#00f0ff]/40 pointer-events-none" />
          <select name={name} value={value} onChange={onChange} style={LABEL_STYLE}
            className={`${base} ${borderCls} pl-9 pr-4 appearance-none cursor-pointer`}>
            <option value="" className="bg-[#0a0a0f]">{placeholder}</option>
            {options.map((opt) => (
              <option key={opt} value={opt} className="bg-[#0a0a0f]">{opt}</option>
            ))}
          </select>
        </div>
        {error && <p className="text-[10px] text-red-400 mt-1" style={LABEL_STYLE}>{error}</p>}
      </div>
    );
  }

  if (type === 'textarea') {
    return (
      <div>
        <div className="relative">
          <Icon size={14} className="absolute left-3 top-4 text-[#00f0ff]/40 pointer-events-none" />
          <textarea name={name} value={value} onChange={onChange} placeholder={placeholder}
            rows={3} style={LABEL_STYLE} className={`${base} ${borderCls} pl-9 pr-4 resize-none`} />
        </div>
        {error && <p className="text-[10px] text-red-400 mt-1" style={LABEL_STYLE}>{error}</p>}
      </div>
    );
  }

  return (
    <div>
      <div className="relative">
        <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#00f0ff]/40 pointer-events-none" />
        <input type={type} name={name} value={value} onChange={onChange} placeholder={placeholder}
          style={LABEL_STYLE} className={`${base} ${borderCls} pl-9 pr-4`} />
      </div>
      {error && <p className="text-[10px] text-red-400 mt-1" style={LABEL_STYLE}>{error}</p>}
    </div>
  );
}

export default function DealerApplyModal({ isOpen, onClose }) {
  const { lang } = useLanguage();
  const lt = APPLY_T[lang] || APPLY_T.en;

  const [form, setForm] = useState({ name: '', email: '', phone: '', country: '', businessType: '', message: '' });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setTimeout(() => {
        setForm({ name: '', email: '', phone: '', country: '', businessType: '', message: '' });
        setErrors({});
        setSuccess(false);
      }, 300);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (errors[e.target.name]) setErrors({ ...errors, [e.target.name]: '' });
  };

  const validate = () => {
    const errs = {};
    if (!form.name.trim())    errs.name = lt.required;
    if (!form.email.trim())   errs.email = lt.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = lt.invalidEmail;
    if (!form.phone.trim())   errs.phone = lt.required;
    if (!form.country.trim()) errs.country = lt.required;
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }

    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/dealer/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || 'Submission failed');
      }
      setSuccess(true);
    } catch (err) {
      setErrors({ submit: err.message });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative w-full max-w-xl mx-4 max-h-[90vh] overflow-y-auto bg-[#0a0a0f] border border-[#39ff14]/20"
        style={{ boxShadow: '0 0 60px rgba(57,255,20,0.08), 0 0 120px rgba(0,240,255,0.04)' }}
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center text-white/40 hover:text-white transition-colors"
        >
          <X size={18} />
        </button>

        <div className="p-6 sm:p-8">
          {success ? (
            /* Success state */
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-5 flex items-center justify-center border border-[#39ff14]/30"
                   style={{ boxShadow: '0 0 30px rgba(57,255,20,0.1)' }}>
                <CheckCircle size={30} className="text-[#39ff14]" />
              </div>
              <h2 className="text-2xl font-black text-white mb-3" style={{ fontFamily: 'Orbitron' }}>
                {lt.successTitle}
              </h2>
              <p className="text-white/55 max-w-sm mx-auto mb-8 text-sm leading-relaxed">
                {lt.successDesc}
              </p>
              <button
                type="button"
                onClick={onClose}
                className="px-8 py-3 bg-[#39ff14] text-[#0a0a0f] font-bold text-xs tracking-[0.2em] transition-all hover:shadow-[0_0_25px_rgba(57,255,20,0.3)]"
                style={{ fontFamily: 'Orbitron' }}
              >
                {lt.successClose}
              </button>
            </div>
          ) : (
            /* Form */
            <>
              <div className="mb-6 pr-8">
                <h2 className="text-xl md:text-2xl font-black text-white mb-2" style={{ fontFamily: 'Orbitron' }}>
                  {lt.title}
                </h2>
                <p className="text-white/45 text-sm">{lt.subtitle}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-3" noValidate>
                <div className="grid sm:grid-cols-2 gap-3">
                  <FormField name="name" value={form.name} onChange={handleChange}
                    placeholder={lt.name} icon={FIELD_ICONS.name} error={errors.name} />
                  <FormField name="email" type="email" value={form.email} onChange={handleChange}
                    placeholder={lt.email} icon={FIELD_ICONS.email} error={errors.email} />
                </div>
                <div className="grid sm:grid-cols-2 gap-3">
                  <FormField name="phone" type="tel" value={form.phone} onChange={handleChange}
                    placeholder={lt.phone} icon={FIELD_ICONS.phone} error={errors.phone} />
                  <FormField name="country" value={form.country} onChange={handleChange}
                    placeholder={lt.country} icon={FIELD_ICONS.country} error={errors.country} />
                </div>
                <FormField name="businessType" value={form.businessType} onChange={handleChange}
                  placeholder={lt.businessType} icon={FIELD_ICONS.businessType} error={errors.businessType}
                  options={lt.businessTypes} />
                <FormField name="message" type="textarea" value={form.message} onChange={handleChange}
                  placeholder={lt.message} icon={FIELD_ICONS.message} error={errors.message} />

                {errors.submit && (
                  <div className="text-[11px] text-red-400/90 bg-red-500/8 border border-red-500/18 px-3 py-2" style={LABEL_STYLE}>
                    {errors.submit}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-4 flex items-center justify-center gap-3 font-bold text-xs tracking-[0.25em] transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    fontFamily: 'Orbitron',
                    background: 'rgba(57,255,20,0.1)',
                    border: '1px solid rgba(57,255,20,0.4)',
                    color: '#39ff14',
                    boxShadow: loading ? 'none' : '0 0 20px rgba(57,255,20,0.1)',
                  }}
                >
                  {loading ? (
                    <><Loader2 size={15} className="animate-spin" /> {lt.submitting}</>
                  ) : (
                    <><Send size={14} /> {lt.submit}</>
                  )}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
